import { useState, useRef, useEffect } from "react";
import { ConfirmationResult, RecaptchaVerifier } from "firebase/auth";
import { setupRecaptcha, sendOtp, signInWithGoogle, signInWithEmail, signUpWithEmail, sendPasswordReset, sendEmailVerification, syncUserToDb } from "@/lib/firebase";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { AlertCircle, CheckCircle, Loader2, Phone, Mail, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";

interface PhoneLoginProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type AuthMethod = "phone" | "google" | "email";
type PhoneStep = "input" | "otp";
type EmailMode = "login" | "signup" | "forgot";

/** Map Firebase error codes to user-friendly messages */
function firebaseErrorMessage(code: string): string {
  const map: Record<string, string> = {
    "auth/too-many-requests": "Too many attempts. Please try again later.",
    "auth/invalid-phone-number": "Invalid phone number format.",
    "auth/invalid-verification-code": "Invalid OTP. Please check and try again.",
    "auth/code-expired": "OTP has expired. Please resend.",
    "auth/user-not-found": "No account found with this email.",
    "auth/wrong-password": "Incorrect password. Please try again.",
    "auth/email-already-in-use": "An account with this email already exists. Try logging in.",
    "auth/weak-password": "Password must be at least 6 characters.",
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/invalid-credential": "Incorrect email or password.",
    "auth/popup-closed-by-user": "Sign-in popup was closed. Please try again.",
    "auth/account-exists-with-different-credential": "An account already exists with this email using a different sign-in method.",
  };
  return map[code] || "Something went wrong. Please try again.";
}

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const PhoneLogin = ({ open, onOpenChange }: PhoneLoginProps) => {
  // Shared
  const [activeMethod, setActiveMethod] = useState<AuthMethod>("phone");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const anyLoading = phoneLoading || googleLoading || emailLoading;

  // Phone OTP state
  const [phone, setPhone] = useState("");
  const [phoneStep, setPhoneStep] = useState<PhoneStep>("input");
  const [otp, setOtp] = useState("");
  const [countdown, setCountdown] = useState(0);
  const confirmationRef = useRef<ConfirmationResult | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);

  // Email state
  const [emailMode, setEmailMode] = useState<EmailMode>("login");
  const [emailValue, setEmailValue] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setActiveMethod("phone");
      setSuccess(false);
      setError("");
      setPhoneLoading(false);
      setGoogleLoading(false);
      setEmailLoading(false);
      setPhone("");
      setPhoneStep("input");
      setOtp("");
      setCountdown(0);
      setEmailMode("login");
      setEmailValue("");
      setPassword("");
      setShowPassword(false);
      setResetSent(false);
      confirmationRef.current = null;
      if (recaptchaRef.current) {
        try { recaptchaRef.current.clear(); } catch {}
        recaptchaRef.current = null;
      }
    }
  }, [open]);

  const handleSuccess = () => {
    setSuccess(true);
    setTimeout(() => onOpenChange(false), 1200);
  };

  // ── Phone OTP handlers ──────────────────────────────────

  const handleSendOtp = async () => {
    setError("");
    if (!/^[6-9]\d{9}$/.test(phone)) {
      setError("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setPhoneLoading(true);
    try {
      if (!recaptchaRef.current) {
        recaptchaRef.current = setupRecaptcha("recaptcha-container");
      }
      const confirmation = await sendOtp(phone, recaptchaRef.current);
      confirmationRef.current = confirmation;
      setPhoneStep("otp");
      setCountdown(30);
    } catch (err: any) {
      setError(firebaseErrorMessage(err.code));
      if (recaptchaRef.current) {
        try { recaptchaRef.current.clear(); } catch {}
        recaptchaRef.current = null;
      }
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    if (otp.length !== 6) { setError("Enter the 6-digit OTP"); return; }
    setPhoneLoading(true);
    try {
      if (!confirmationRef.current) { setError("Session expired. Please resend OTP."); setPhoneStep("input"); return; }
      await confirmationRef.current.confirm(otp);
      handleSuccess();
    } catch (err: any) {
      setError(firebaseErrorMessage(err.code));
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleResend = async () => {
    setOtp(""); setError("");
    if (recaptchaRef.current) {
      try { recaptchaRef.current.clear(); } catch {}
      recaptchaRef.current = null;
    }
    await handleSendOtp();
  };

  // ── Google handler ──────────────────────────────────────

  const handleGoogle = async () => {
    setError(""); setGoogleLoading(true);
    try {
      await signInWithGoogle();
      handleSuccess();
    } catch (err: any) {
      console.error("Google sign-in failed:", err.code, err.message);
      if (err.code !== "auth/popup-closed-by-user") {
        setError(firebaseErrorMessage(err.code));
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  // ── Email handlers ──────────────────────────────────────

  const handleEmailSubmit = async () => {
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      setError("Please enter a valid email address"); return;
    }

    if (emailMode === "forgot") {
      setEmailLoading(true);
      try {
        await sendPasswordReset(emailValue);
        setResetSent(true);
      } catch (err: any) {
        setError(firebaseErrorMessage(err.code));
      } finally {
        setEmailLoading(false);
      }
      return;
    }

    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }

    setEmailLoading(true);
    try {
      if (emailMode === "signup") {
        const result = await signUpWithEmail(emailValue, password);
        // Send verification email
        try {
          await sendEmailVerification(result.user);
        } catch (verifyErr) {
          console.warn("Could not send verification email:", verifyErr);
        }
      } else {
        await signInWithEmail(emailValue, password);
      }
      handleSuccess();
    } catch (err: any) {
      setError(firebaseErrorMessage(err.code));
    } finally {
      setEmailLoading(false);
    }
  };

  // ── Success screen ──────────────────────────────────────

  if (success) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-secondary" /> Welcome!
            </DialogTitle>
            <DialogDescription className="font-body text-muted-foreground">
              You're now logged in
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6 gap-3 text-center">
            <CheckCircle className="w-14 h-14 text-secondary" />
            <p className="font-heading text-lg font-bold text-foreground">Login Successful!</p>
            <p className="font-body text-sm text-muted-foreground">Redirecting...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Main modal ──────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-foreground flex items-center gap-2">
            {activeMethod === "phone" && <Phone className="w-5 h-5 text-primary" />}
            {activeMethod === "email" && <Mail className="w-5 h-5 text-primary" />}
            {activeMethod === "google" && <GoogleIcon />}
            Login / Sign Up
          </DialogTitle>
          <DialogDescription className="font-body text-muted-foreground">
            Choose your preferred login method
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">

          {/* ── PHONE OTP (Primary) ──────────────────────── */}
          {activeMethod === "phone" && (
            <>
              {phoneStep === "input" && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="login-phone" className="font-body text-sm text-foreground">Phone Number</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-border bg-muted text-muted-foreground text-sm">+91</span>
                      <Input
                        id="login-phone"
                        placeholder="98XXXXXXXX"
                        value={phone}
                        onChange={(e) => { setPhone(e.target.value.replace(/\D/g, "").slice(0, 10)); if (error) setError(""); }}
                        onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                        className="rounded-l-none bg-muted border-border text-foreground placeholder:text-muted-foreground"
                        disabled={anyLoading}
                      />
                    </div>
                  </div>

                  {error && (
                    <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</p>
                  )}

                  <motion.button type="button" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    onClick={handleSendOtp} disabled={phoneLoading || phone.length !== 10}
                    className="w-full font-heading text-sm font-semibold px-8 py-3.5 rounded-full bg-primary text-primary-foreground glow-magenta transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {phoneLoading ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Sending OTP...</span> : "Send OTP"}
                  </motion.button>
                </>
              )}

              {phoneStep === "otp" && (
                <>
                  <p className="font-body text-sm text-muted-foreground">We sent a 6-digit code to <strong>+91 {phone}</strong></p>
                  <div className="space-y-3">
                    <Label className="font-body text-sm text-foreground">Enter OTP</Label>
                    <div className="flex justify-center">
                      <InputOTP maxLength={6} value={otp} onChange={(val) => { setOtp(val); if (error) setError(""); }}>
                        <InputOTPGroup>
                          <InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} />
                          <InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  </div>

                  {error && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</p>}

                  <motion.button type="button" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    onClick={handleVerifyOtp} disabled={phoneLoading || otp.length !== 6}
                    className="w-full font-heading text-sm font-semibold px-8 py-3.5 rounded-full bg-primary text-primary-foreground glow-magenta transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {phoneLoading ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</span> : "Verify & Login"}
                  </motion.button>

                  <div className="text-center">
                    {countdown > 0 ? (
                      <p className="text-xs text-muted-foreground">Resend OTP in {countdown}s</p>
                    ) : (
                      <button onClick={handleResend} disabled={anyLoading} className="text-xs text-primary hover:underline font-body disabled:opacity-50">Resend OTP</button>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* ── EMAIL / PASSWORD ─────────────────────────── */}
          {activeMethod === "email" && (
            <>
              {emailMode === "forgot" ? (
                <>
                  <div className="space-y-1.5">
                    <Label className="font-body text-sm text-foreground">Email Address</Label>
                    <Input type="email" placeholder="you@example.com" value={emailValue}
                      onChange={(e) => { setEmailValue(e.target.value); if (error) setError(""); setResetSent(false); }}
                      onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground" disabled={anyLoading}
                    />
                  </div>

                  {resetSent && <p className="text-xs text-secondary flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Password reset link sent! Check your inbox.</p>}
                  {error && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</p>}

                  <motion.button type="button" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    onClick={handleEmailSubmit} disabled={emailLoading || !emailValue}
                    className="w-full font-heading text-sm font-semibold px-8 py-3.5 rounded-full bg-primary text-primary-foreground glow-magenta transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {emailLoading ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Sending...</span> : "Send Reset Link"}
                  </motion.button>

                  <button onClick={() => { setEmailMode("login"); setError(""); setResetSent(false); }}
                    className="text-xs text-primary hover:underline font-body w-full text-center">← Back to login</button>
                </>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label className="font-body text-sm text-foreground">Email Address</Label>
                    <Input type="email" placeholder="you@example.com" value={emailValue}
                      onChange={(e) => { setEmailValue(e.target.value); if (error) setError(""); }}
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground" disabled={anyLoading}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="font-body text-sm text-foreground">Password</Label>
                    <div className="relative">
                      <Input type={showPassword ? "text" : "password"} placeholder="Min 6 characters" value={password}
                        onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
                        onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
                        className="bg-muted border-border text-foreground placeholder:text-muted-foreground pr-10" disabled={anyLoading}
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {emailMode === "login" && (
                    <button onClick={() => { setEmailMode("forgot"); setError(""); }}
                      className="text-xs text-primary hover:underline font-body">Forgot password?</button>
                  )}

                  {error && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</p>}

                  <motion.button type="button" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    onClick={handleEmailSubmit} disabled={emailLoading || !emailValue || !password}
                    className="w-full font-heading text-sm font-semibold px-8 py-3.5 rounded-full bg-primary text-primary-foreground glow-magenta transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {emailLoading ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {emailMode === "signup" ? "Creating account..." : "Logging in..."}</span>
                      : emailMode === "signup" ? "Create Account" : "Login"}
                  </motion.button>

                  <p className="text-xs text-muted-foreground text-center font-body">
                    {emailMode === "login" ? (
                      <>Don't have an account? <button onClick={() => { setEmailMode("signup"); setError(""); }} className="text-primary hover:underline">Sign up</button></>
                    ) : (
                      <>Already have an account? <button onClick={() => { setEmailMode("login"); setError(""); }} className="text-primary hover:underline">Log in</button></>
                    )}
                  </p>
                </>
              )}
            </>
          )}

          {/* ── DIVIDER ──────────────────────────────────── */}
          {activeMethod !== "email" && (
            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground font-body">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>
          )}

          {/* ── GOOGLE BUTTON ────────────────────────────── */}
          {activeMethod !== "email" && (
            <motion.button type="button" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              onClick={handleGoogle} disabled={anyLoading}
              className="w-full flex items-center justify-center gap-3 px-8 py-3 rounded-full border border-border bg-muted hover:bg-muted/80 text-foreground font-heading text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <GoogleIcon />
              Continue with Google
            </motion.button>
          )}

          {/* ── EMAIL TOGGLE LINK ────────────────────────── */}
          {activeMethod !== "email" && (
            <button onClick={() => { setActiveMethod("email"); setError(""); }}
              className="w-full text-center text-xs text-primary hover:underline font-body flex items-center justify-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Use Email instead
            </button>
          )}

          {/* ── BACK TO PHONE (from email) ───────────────── */}
          {activeMethod === "email" && (
            <>
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground font-body">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              <motion.button type="button" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                onClick={handleGoogle} disabled={anyLoading}
                className="w-full flex items-center justify-center gap-3 px-8 py-3 rounded-full border border-border bg-muted hover:bg-muted/80 text-foreground font-heading text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <GoogleIcon />
                Continue with Google
              </motion.button>

              <button onClick={() => { setActiveMethod("phone"); setError(""); setEmailMode("login"); }}
                className="w-full text-center text-xs text-primary hover:underline font-body flex items-center justify-center gap-1.5">
                <Phone className="w-3.5 h-3.5" /> Use Phone instead
              </button>
            </>
          )}

          {/* Global error for Google */}
          {activeMethod !== "phone" && activeMethod !== "email" && error && (
            <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</p>
          )}
        </div>

        {/* Invisible recaptcha container */}
        <div id="recaptcha-container" />
      </DialogContent>
    </Dialog>
  );
};

export default PhoneLogin;
